/*
 * Dashboard operations for the Demo Environment Management page.
 * Environment-level entity. Mirrors the dashboard-related subset of
 * sales-demo-env-setup's `src/lib/chart.js`, adapted to use `CsqApiClient`.
 */

/**
 * Creates an empty dashboard.
 *   POST /api/dashboards/v4/projects/{environmentId}/users/me/dashboards
 *
 * Deliberately v4, not v5, for consistency with list/delete above (same v4/v5 resource-version
 * split as the rest of this file). Unlike v5 (a flat dashboard object), v4's create response
 * nests the dashboard under `dashboard` (alongside `dashboardUserContext`/`permissions`) —
 * confirmed live (real create-dashboard response only has `payload.dashboard.id`, not
 * `payload.id`) — so it's unwrapped here to keep this function's return value a flat dashboard
 * object, matching what every caller expects.
 *
 * @returns {Promise<object>} The created dashboard, including `id`.
 */
export async function createDashboard(client, environmentId, name = 'New dashboard') {
  const payload = await client.post(`/api/dashboards/v4/projects/${environmentId}/users/me/dashboards`, { name });
  return (payload && payload.dashboard) || payload;
}

/**
 * Creates an empty dashboard, same as `createDashboard` above but via v5 instead of v4.
 *   POST /api/dashboards/v5/projects/{environmentId}/users/me/dashboards
 *
 * Needed specifically for a dashboard that will receive a chart via the legacy
 * `blocks/append-chart-block` v1 endpoint (see csqCharts.js/appendChartToDashboard): a
 * dashboard created via v4 causes that v1 call to fail with a 500 `UNKNOWN_ERROR`, whereas the
 * real ContentSquare app creates the dashboard via v5 right before attaching a chart to it
 * (confirmed live via HAR capture) — so this mirrors that exact call. v5's create response is
 * already flat (`payload.id`, not `payload.dashboard.id` like v4), so no unwrapping is needed.
 *
 * @returns {Promise<object>} The created dashboard, including `id`.
 */
export function createDashboardForChart(client, environmentId, name = 'New dashboard') {
  return client.post(`/api/dashboards/v5/projects/${environmentId}/users/me/dashboards`, { name });
}

/**
 * Lists the current user's dashboards in an environment.
 *   GET /api/dashboards/v4/projects/{environmentId}/users/me/dashboards?withPermissions=true
 *   GET /api/dashboards/v5/projects/{environmentId}/users/me/dashboards
 *
 * Queries BOTH v4 and v5 and merges the results (deduped by id). Dashboards can be created under
 * either version — v4 for the widget-groups/widgets model (see `createDashboard` above) or v5 for
 * a dashboard about to receive a chart via the legacy blocks model (see `createDashboardForChart`
 * above) — and each version's list endpoint only returns dashboards created under that same
 * version, confirmed live via dev tools (the real ContentSquare app itself issues both a v4 and a
 * v5 list call when loading the dashboards page). Querying only one version means list/delete/
 * recreate silently miss whichever dashboards were created under the other one.
 *
 * Deliberately v4 + withPermissions=true (not a bare v4 list): a plain v4 list with no query param
 * only returns dashboards owned by the exact authenticated API user, so it comes back empty
 * whenever a project's dashboards were created under a different session/user (e.g. via the UI, or
 * by another demo-setup run) — confirmed against real ContentSquare app traffic.
 *
 * Tolerates either version failing (e.g. no access to that resource) as long as the other
 * succeeds; only throws if both fail.
 *
 * Each returned dashboard carries a `version` field (`'v4'` or `'v5'`) recording which
 * generation it was found under, since the two are NOT interchangeable for other operations:
 * a v5 dashboard has no widget-groups/widgets (v2/v3 endpoints 500 for it), and a v4 dashboard
 * is not the right target for the v1 append-chart-block flow (see `resolveDashboardId`'s
 * `forChart` option and `createAllEntityTypes` in csqEntityHandlers.js, which both branch on
 * this field to decide how to recreate/populate a given dashboard).
 *
 * @returns {Promise<Array<{ id: string, name: string, version: 'v4'|'v5' }>>}
 */
export async function listDashboards(client, environmentId) {
  const [v4Result, v5Result] = await Promise.allSettled([
    client.get(`/api/dashboards/v4/projects/${environmentId}/users/me/dashboards?withPermissions=true`),
    client.get(`/api/dashboards/v5/projects/${environmentId}/users/me/dashboards`),
  ]);
  if (v4Result.status === 'rejected' && v5Result.status === 'rejected') {
    throw v4Result.reason;
  }

  const toArray = (payload) => (Array.isArray(payload) ? payload : (payload && payload.items) || []);
  const rawDashboards = [
    ...(v4Result.status === 'fulfilled' ? toArray(v4Result.value).map((dashboard) => ({ dashboard, version: 'v4' })) : []),
    ...(v5Result.status === 'fulfilled' ? toArray(v5Result.value).map((dashboard) => ({ dashboard, version: 'v5' })) : []),
  ];

  const dashboardsById = new Map();
  rawDashboards.forEach(({ dashboard, version }) => {
    if (dashboard && dashboard.id && !dashboardsById.has(dashboard.id)) {
      dashboardsById.set(dashboard.id, { id: dashboard.id, name: dashboard.name || '(unnamed)', version });
    }
  });
  return [...dashboardsById.values()];
}

/**
 * Lists a dashboard's legacy chart blocks (the older `charts` + `blocks` model, see
 * csqCharts.js/appendChartToDashboard). Used only to discover which dashboard(s) a chart is
 * grouped under on the source project, so `createAllEntityTypes` can replicate that grouping
 * instead of dumping every replicated chart into its own brand new dashboard.
 *   GET /api/dashboards/v1/projects/{environmentId}/users/me/dashboards/{dashboardId}/blocks
 *
 * @returns {Promise<Array<{ chartId: string }>>} Raw blocks — only chart-type blocks have a `chartId`.
 */
export async function listDashboardBlocks(client, environmentId, dashboardId) {
  const payload = await client.get(`/api/dashboards/v1/projects/${environmentId}/users/me/dashboards/${dashboardId}/blocks`);
  return Array.isArray(payload) ? payload : (payload && payload.items) || [];
}

/**
 * Deletes a single dashboard.
 *   DELETE /api/dashboards/v4/projects/{environmentId}/users/me/dashboards/{dashboardId}
 *   DELETE /api/dashboards/v5/projects/{environmentId}/users/me/dashboards/{dashboardId}
 *
 * Calls BOTH v4 and v5 delete, since (like list above) a dashboard may have been created under
 * either version, and deleting via the "wrong" version for a given dashboard has been observed to
 * return a "success" response without actually removing it (confirmed live — deleting via v5 alone
 * logged as OK for every item, yet the dashboards were still visible afterwards via the v4 list).
 * Only throws if both calls fail; a single-version failure (e.g. 404 because that dashboard was
 * never created under that version) is expected and ignored.
 */
export async function deleteDashboard(client, environmentId, dashboardId) {
  const results = await Promise.allSettled([
    client.del(`/api/dashboards/v4/projects/${environmentId}/users/me/dashboards/${dashboardId}`),
    client.del(`/api/dashboards/v5/projects/${environmentId}/users/me/dashboards/${dashboardId}`),
  ]);
  if (results.every((result) => result.status === 'rejected')) {
    throw results[0].reason;
  }
}

/**
 * Resolves a target dashboard id. An explicit `dashboardId` is used as-is;
 * otherwise a new dashboard is created (named `dashboardName`, default
 * "New dashboard").
 *
 * Pass `forChart: true` when the resolved dashboard is going to receive a chart via
 * `appendChartToDashboard` (csqCharts.js) — this creates the dashboard via v5 instead of v4 (see
 * `createDashboardForChart` above), which is required for that v1 attach call to succeed.
 *
 * @returns {Promise<string>} The resolved dashboard id.
 */
export async function resolveDashboardId(client, environmentId, { dashboardId, dashboardName, forChart = false, log = () => {} } = {}) {
  if (dashboardId) return dashboardId;

  const name = dashboardName || 'New dashboard';
  log(`No dashboard id provided — creating dashboard "${name}"...`);
  const dashboard = forChart
    ? await createDashboardForChart(client, environmentId, name)
    : await createDashboard(client, environmentId, name);
  const id = dashboard && dashboard.id;
  if (!id) {
    throw new Error(`Unexpected create-dashboard response: ${JSON.stringify(dashboard)}`);
  }
  return id;
}

/*
 * Widget-group / widget operations. Every current dashboard (both ContentSquare's built-in
 * template dashboards like "RUM - Core Web Vitals" and user-created custom dashboards) is built
 * from this same model: a dashboard has widget groups (sections), each group has widgets (KPI
 * cards, line charts, tables, text blocks, etc.) positioned within it. Confirmed against real
 * ContentSquare app traffic (HAR captures): GET v2 widget-groups / GET v3 widgets for reading,
 * POST v2 widget-groups / POST v3 widgets for creating. The older `charts` + `blocks` model (v1,
 * see csqCharts.js/appendChartToDashboard) is not used by the current UI for either template or
 * custom dashboards — real dashboards observed live all used widget-groups/widgets instead.
 */

/**
 * Lists a dashboard's widget groups (sections).
 *   GET /api/dashboards/v2/projects/{environmentId}/users/me/dashboards/{dashboardId}/widget-groups
 *
 * @returns {Promise<Array<{ id: string, name: string, order: number, expanded: boolean }>>}
 */
export async function listWidgetGroups(client, environmentId, dashboardId) {
  const payload = await client.get(`/api/dashboards/v2/projects/${environmentId}/users/me/dashboards/${dashboardId}/widget-groups`);
  return Array.isArray(payload) ? payload : (payload && payload.items) || [];
}

/**
 * Creates one or more widget groups on a dashboard, in one call.
 *   POST /api/dashboards/v2/projects/{environmentId}/users/me/dashboards/{dashboardId}/widget-groups
 *
 * @param {Array<{ name: string, order: number, expanded?: boolean }>} groups
 * @returns {Promise<Array<object>>} The created groups, each including `id` (same order as input).
 */
export function createWidgetGroups(client, environmentId, dashboardId, groups) {
  return client.post(`/api/dashboards/v2/projects/${environmentId}/users/me/dashboards/${dashboardId}/widget-groups`, groups);
}

/**
 * Lists a dashboard's widgets.
 *   GET /api/dashboards/v3/projects/{environmentId}/users/me/dashboards/{dashboardId}/widgets
 *
 * @returns {Promise<Array<object>>} Raw widget objects (id, name, type, dimensions, config, groupId, groupPosition, ...).
 */
export async function listWidgets(client, environmentId, dashboardId) {
  const payload = await client.get(`/api/dashboards/v3/projects/${environmentId}/users/me/dashboards/${dashboardId}/widgets`);
  return Array.isArray(payload) ? payload : (payload && payload.items) || [];
}

/**
 * Creates one or more widgets on a dashboard, in one call.
 *   POST /api/dashboards/v3/projects/{environmentId}/users/me/dashboards/{dashboardId}/widgets
 *
 * @param {Array<{ name: string, type: string, config: object, dimensions: object, groupId: string, groupPosition: object }>} widgets
 * @returns {Promise<Array<object>>} The created widgets, each including a new `id`.
 */
export function createWidgets(client, environmentId, dashboardId, widgets) {
  return client.post(`/api/dashboards/v3/projects/${environmentId}/users/me/dashboards/${dashboardId}/widgets`, widgets);
}

/**
 * Copies a source dashboard's full content (widget groups + widgets) onto an already-created
 * target dashboard. Source widget group ids are remapped to the newly created target group ids so
 * each widget lands in the group matching its source position. `sourceClient`/`targetClient` may
 * be the same client when source and target are the same ContentSquare account/session.
 *
 * @returns {Promise<{ groupCount: number, widgetCount: number }>}
 */
export async function copyDashboardContent(
  sourceClient,
  targetClient,
  { sourceProjectId, sourceDashboardId, targetProjectId, targetDashboardId, log = () => {} },
) {
  const [sourceGroups, sourceWidgets] = await Promise.all([
    listWidgetGroups(sourceClient, sourceProjectId, sourceDashboardId),
    listWidgets(sourceClient, sourceProjectId, sourceDashboardId),
  ]);

  if (sourceGroups.length === 0) {
    log('no widget groups to copy.');
    return { groupCount: 0, widgetCount: 0 };
  }

  const newGroups = await createWidgetGroups(
    targetClient,
    targetProjectId,
    targetDashboardId,
    sourceGroups.map((group) => ({ name: group.name, order: group.order, expanded: group.expanded })),
  );
  log(`created ${newGroups.length} widget group(s).`);

  const groupIdMap = new Map(sourceGroups.map((group, index) => [group.id, newGroups[index] && newGroups[index].id]));

  const widgetsToCreate = sourceWidgets
    .filter((widget) => groupIdMap.has(widget.groupId))
    .map((widget) => ({
      name: widget.name,
      type: widget.type,
      config: widget.config,
      dimensions: widget.dimensions,
      groupId: groupIdMap.get(widget.groupId),
      groupPosition: widget.groupPosition,
    }));

  if (widgetsToCreate.length === 0) {
    log('no widgets to copy.');
    return { groupCount: newGroups.length, widgetCount: 0 };
  }

  const newWidgets = await createWidgets(targetClient, targetProjectId, targetDashboardId, widgetsToCreate);
  log(`created ${newWidgets.length} widget(s).`);

  return { groupCount: newGroups.length, widgetCount: newWidgets.length };
}
